import React, { useEffect, useState, ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { createAuthTokensService, getAuthTokensService } from '../lib/auth/AuthTokensService';
import { setupAuthInterceptor } from '../lib/auth/authInterceptor';
import { useAuth as useIframeAuth } from '../lib/auth/useAuth';
import { initializeHiddenIframeRefresh } from '../lib/auth/hiddenIframeRefresh';
import { authService } from '../lib/auth/authService';
import { authApi, type User, type LoginRequest, api } from '../lib/api';
import toast from 'react-hot-toast';
import { useIntercom } from '@darwin-launchpad/legacy-shared/intercom';
import { useDarwinFeedbackToolbar } from '@darwin-launchpad/legacy-shared/feedback-toolbar';
import { initTelemetry, setTelemetryUser, clearTelemetryUser } from '@darwin-launchpad/legacy-shared/telemetry';
import { launchpadApiBaseUrl } from '../lib/usersSettings';
import axios from 'axios';
import { IframeAuthContext, type IframeAuthContextType } from './IframeAuthContext';

initTelemetry({
  service: 'redactit',
  datadogService: 'darwin-launchpad-redactit',
  sessionReplay: {
    sampleRate: { production: 0.05, staging: 1 },
    // Production replay is on like every other app, but the rendered
    // document is never recorded. The PDFViewer canvas root carries
    // `data-replay-block`; toolbars, entity lists and dialogs stay
    // visible (masked at the lane's mask level).
  },
});

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message || fallback;
  }
  return fallback;
};

interface IframeAuthProviderProps {
  children: ReactNode;
  authDomain: string;
  apiBaseUrl: string;
  onNavigateToLogin: () => void;
  getQueryParam?: (param: string) => string | null;
  darwinLoginUrl?: string;
}

export const IframeAuthProvider: React.FC<IframeAuthProviderProps> = ({
  children,
  authDomain,
  apiBaseUrl,
  onNavigateToLogin,
  getQueryParam,
  darwinLoginUrl,
}) => {
  const queryClient = useQueryClient();
  const [isInitialized, setIsInitialized] = useState(false);
  const [user, setUser] = useState<User | null>(null);

  useIntercom(user);
  useDarwinFeedbackToolbar(user, {
    appId: 'redactit',
    apiBaseUrl: launchpadApiBaseUrl(),
    getAccessToken: () => getAuthTokensService().getCurrentCredentials()?.access_token?.token,
    load: () => import('@darwin/feedback-embed/customer'),
  });

  // Initialize auth service and interceptor
  useEffect(() => {
    const isDevBypass = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';

    createAuthTokensService(authDomain, darwinLoginUrl);

    // Initialize hidden iframe refresh service (skip in dev bypass mode)
    if (darwinLoginUrl && !isDevBypass) {
      initializeHiddenIframeRefresh(authDomain, darwinLoginUrl);
    }

    setupAuthInterceptor(
      apiBaseUrl,
      authService,
      onNavigateToLogin,
      getQueryParam,
      api
    );
  }, [authDomain, apiBaseUrl, onNavigateToLogin, getQueryParam, darwinLoginUrl]);

  // Use iframe auth hook
  const { credentials, rolesFromToken, authTenantId, isLoading: isIframeLoading, isAuthenticated } = useIframeAuth();

  // Fetch user data when credentials are available
  const {
    data: userData,
    isLoading: isUserLoading,
  } = useQuery(
    // Keyed on the token: a /auth/me that failed on a stale token (401, then a
    // refused refresh) runs again once a working one arrives. With the plain
    // key and retry: false it never re-ran, and the session stayed anonymous.
    ['current-user', credentials?.access_token?.token],
    authApi.getCurrentUser,
    {
      enabled: isAuthenticated && !!credentials?.access_token?.token,
      retry: false,
      onSuccess: (data) => {
        setUser(data);
        setIsInitialized(true);
      },
      onError: (error: unknown) => {
        console.error('Failed to fetch user:', error);
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          // Token is invalid, let the interceptor handle it
          setUser(null);
        }
        setIsInitialized(true);
      },
    }
  );

  // Initialize immediately if no credentials
  useEffect(() => {
    if (!isAuthenticated && !isIframeLoading && !isInitialized) {
      setIsInitialized(true);
    }
  }, [isAuthenticated, isIframeLoading, isInitialized]);

  // Update user when userData changes
  useEffect(() => {
    if (userData) {
      setUser(userData);
    }
  }, [userData]);

  // Clear user when not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setUser(null);
    }
  }, [isAuthenticated]);

  // Set or clear Datadog user/tenant context based on auth state
  useEffect(() => {
    if (user) {
      // Identify whenever there is a user. The token's tenant is preferred, the
      // profile's otherwise: gating on the token tenant left a signed-in user
      // with none unidentified for the whole session.
      const profile = user as { tenant_id?: string; current_tenant?: { id?: string } };
      setTelemetryUser(user.id, authTenantId || profile.current_tenant?.id || profile.tenant_id || '', {
        tenantName: user.current_tenant?.name,
        userName: [user.first_name, user.last_name].filter(Boolean).join(' ') || undefined,
        userEmail: user.email,
        roles: user.roles ?? user.current_tenant?.roles ?? rolesFromToken ?? undefined,
      });
    } else {
      clearTelemetryUser();
    }
  }, [user, authTenantId]);

  // Login mutation
  const loginMutation = useMutation(authApi.login, {
    onSuccess: async (data) => {
      // Store tokens in iframe
      const authTokensService = createAuthTokensService(authDomain, darwinLoginUrl);

      let accessTokenData;

      // Check if data.access_token is already an object with exp field (Darwin format)
      if (typeof data.access_token === 'object' && 'exp' in data.access_token) {
        accessTokenData = data.access_token;
      } else {
        // access_token is a string JWT
        const tokenString = data.access_token as string;

        // Decode JWT to extract expiration time and other fields
        let exp = '';
        let roles = data.user?.roles || [];
        let tenant_id = data.user?.tenant_id || data.user?.current_tenant?.id || '';

        try {
          const decodedToken = authTokensService.decodeToken(tokenString);
          exp = new Date(decodedToken.exp * 1000).toISOString();
          roles = decodedToken.roles || roles;
          tenant_id = decodedToken.tenant_id || tenant_id;
        } catch (error) {
          console.warn('Failed to decode JWT token during login:', error);
        }

        accessTokenData = {
          token: tokenString,
          roles: roles,
          tenant_id: tenant_id,
          exp: exp,
        };
      }

      await authTokensService.setCredentials({
        access_token: accessTokenData,
        refresh_token: {
          token: data.refresh_token,
          exp: '', // Will be populated from JWT if needed
        },
      });

      setUser(data.user);
      queryClient.setQueryData('current-user', data.user);
      toast.success('Successfully logged in');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Login failed'));
    },
  });

  // Logout function
  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      queryClient.clear();
      toast.success('Successfully logged out');
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Logout failed'));
    }
  };

  // Update profile mutation
  const updateProfileMutation = useMutation(authApi.updateProfile, {
    onSuccess: (data) => {
      setUser(data);
      queryClient.setQueryData('current-user', data);
      toast.success('Profile updated successfully');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to update profile'));
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation(authApi.changePassword, {
    onSuccess: () => {
      toast.success('Password changed successfully');
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, 'Failed to change password'));
    },
  });

  // Context methods
  const login = async (credentials: LoginRequest) => {
    await loginMutation.mutateAsync(credentials);
  };

  const updateProfile = async (data: Partial<User>) => {
    await updateProfileMutation.mutateAsync(data);
  };

  const changePassword = async (data: { current_password: string; new_password: string }) => {
    await changePasswordMutation.mutateAsync(data);
  };

  const hasRole = (role: string): boolean => {
    // Check user roles first, fallback to token roles if user not loaded yet
    const userRoles = user?.roles ?? user?.current_tenant?.roles ?? rolesFromToken ?? [];
    return userRoles.includes(role);
  };

  const hasAnyRole = (roles: string[]): boolean => {
    // Check user roles first, fallback to token roles if user not loaded yet
    const userRoles = user?.roles ?? user?.current_tenant?.roles ?? rolesFromToken ?? [];
    return roles.some(role => userRoles.includes(role));
  };

  // Determine loading state
  const isLoading =
    !isInitialized ||
    isIframeLoading ||
    (isAuthenticated && isUserLoading) ||
    loginMutation.isLoading ||
    updateProfileMutation.isLoading ||
    changePasswordMutation.isLoading;

  const value: IframeAuthContextType = {
    user,
    login,
    logout,
    isLoading,
    isAuthenticated: isAuthenticated, // Use iframe auth status directly
    hasRole,
    hasAnyRole,
    updateProfile,
    changePassword,
    credentials,
    rolesFromToken,
    authTenantId,
  };

  return (
    <IframeAuthContext.Provider value={value}>
      {children}
    </IframeAuthContext.Provider>
  );
};

export default IframeAuthProvider;